'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, ReactNode, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import StickyNote from './StickyNote';
import { useTranslation } from '@/context/I18nContext';
import { getPMStickyNotesData } from '@/data/loader';
interface PMTransitionProps {
  isActive: boolean;
  children: ReactNode;
}
export default function PMTransition(t0) {
  const $ = _c(33);
  const {
    isActive,
    children
  } = t0;
  const {
    locale,
    t
  } = useTranslation();
  let t1;
  if ($[0] !== locale) {
    t1 = getPMStickyNotesData(locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stickyNotes = t1;
  const containerRef = useRef(null);
  const whiteboardRef = useRef(null);
  const [isKanbanExpanded, setIsKanbanExpanded] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [dragConstraints, setDragConstraints] = useState(t2);
  const [isDraggingBoard, setIsDraggingBoard] = useState(false);
  let t3;
  let t4;
  if ($[3] !== isActive) {
    t3 = () => {
      if (isActive) {
        document.body.classList.add("pm-whiteboard-active");
        const updateConstraints = () => {
          const width = window.innerWidth;
          const height = window.innerHeight;
          setDragConstraints({
            left: -width + 400,
            right: width - 400,
            top: -height + 200,
            bottom: height - 200
          });
        };
        updateConstraints();
        window.addEventListener("resize", updateConstraints);
        return () => {
          document.body.classList.remove("pm-whiteboard-active");
          window.removeEventListener("resize", updateConstraints);
        };
      }
    };
    t4 = [isActive];
    $[3] = isActive;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== isActive || $[7] !== isDraggingBoard) {
    t5 = () => {
      if (!isActive && isDraggingBoard) {
        requestAnimationFrame(() => {
          setIsDraggingBoard(false);
        });
      }
    };
    t6 = [isActive, isDraggingBoard];
    $[6] = isActive;
    $[7] = isDraggingBoard;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] !== stickyNotes) {
    t7 = stickyNotes.filter(_temp);
    $[10] = stickyNotes;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = t7 as typeof stickyNotes;
  let t9;
  if ($[12] !== stickyNotes) {
    t9 = stickyNotes.filter(_temp2);
    $[12] = stickyNotes;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = t9 as typeof stickyNotes;
  let t11;
  if ($[14] !== stickyNotes) {
    t11 = stickyNotes.filter(_temp3);
    $[14] = stickyNotes;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = t11 as typeof stickyNotes;
  let t13;
  if ($[16] !== t10 || $[17] !== t12 || $[18] !== t8) {
    t13 = {
      BACKLOG: t8,
      IN_PROGRESS: t10,
      DONE: t12
    };
    $[16] = t10;
    $[17] = t12;
    $[18] = t8;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const notesByType = t13;
  let t14;
  if ($[20] !== isActive) {
    t14 = isActive && <div ref={whiteboardRef} className="pm-whiteboard-bg" />;
    $[20] = isActive;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== dragConstraints || $[23] !== isActive || $[24] !== isDraggingBoard || $[25] !== isKanbanExpanded || $[26] !== notesByType || $[27] !== t) {
    t15 = isActive && <motion.div className={`pm-kanban-board-container ${isKanbanExpanded ? "pm-kanban-expanded" : "pm-kanban-minimized"}`} drag={true} dragConstraints={dragConstraints} dragElastic={0.05} dragMomentum={false} dragTransition={{
      bounceStiffness: 600,
      bounceDamping: 20
    }} data-dragging={isDraggingBoard || undefined} onDragStart={() => setIsDraggingBoard(true)} onDragEnd={() => setIsDraggingBoard(false)} whileDrag={{
      scale: 1.02,
      boxShadow: "0 25px 70px rgba(0, 0, 0, 0.2)",
      cursor: "grabbing",
      transition: {
        duration: 0
      }
    }} initial={{
      x: 0,
      y: 0
    }} animate={{
      x: 0,
      y: 0
    }} transition={{
      type: "spring",
      stiffness: 800,
      damping: 30,
      mass: 0.3
    }}><div className="pm-kanban-header pm-drag-handle"><div className="pm-kanban-title"><span className="pm-kanban-icon">📋</span>{isKanbanExpanded && <span>{t("roadmap.title")}</span>}</div><button className="pm-kanban-toggle" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsKanbanExpanded(_temp4);
        }} aria-label={isKanbanExpanded ? t("roadmap.minimizeAriaLabel") : t("roadmap.expandAriaLabel")} type="button"><span className="pm-kanban-toggle-icon">{isKanbanExpanded ? "\u2212" : "+"}</span></button></div>{isKanbanExpanded && <div className="pm-kanban-columns"><div className="pm-kanban-column pm-column-backlog"><div className="pm-column-header"><div className="pm-column-title"><span className="pm-column-icon">📋</span><span>{t("roadmap.backlog")}</span></div><div className="pm-column-count">{notesByType.BACKLOG.length}</div></div><div className="pm-column-body">{notesByType.BACKLOG.map(_temp5)}</div></div><div className="pm-kanban-column pm-column-in-progress"><div className="pm-column-header"><div className="pm-column-title"><span className="pm-column-icon">🚧</span><span>{t("roadmap.inProgress")}</span></div><div className="pm-column-count">{notesByType.IN_PROGRESS.length}</div></div><div className="pm-column-body">{notesByType.IN_PROGRESS.map(_temp6)}</div></div><div className="pm-kanban-column pm-column-done"><div className="pm-column-header"><div className="pm-column-title"><span className="pm-column-icon">✅</span><span>{t("roadmap.done")}</span></div><div className="pm-column-count">{notesByType.DONE.length}</div></div><div className="pm-column-body">{notesByType.DONE.map(_temp7)}</div></div></div>}</motion.div>;
    $[22] = dragConstraints;
    $[23] = isActive;
    $[24] = isDraggingBoard;
    $[25] = isKanbanExpanded;
    $[26] = notesByType;
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== children || $[30] !== t14 || $[31] !== t15) {
    t16 = <div ref={containerRef} className="pm-transition-wrapper">{t14}{t15}{children}</div>;
    $[29] = children;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}
function _temp7(note_4) {
  return <div key={note_4.id} className="pm-sticky-note-wrapper-in-column"><StickyNote id={note_4.id} type={note_4.type} title={note_4.title} content={note_4.content} color={note_4.color} initialX={0} initialY={0} rotation={note_4.rotation} badge={note_4.badge} stamp={note_4.stamp} /></div>;
}
function _temp6(note_3) {
  return <div key={note_3.id} className="pm-sticky-note-wrapper-in-column"><StickyNote id={note_3.id} type={note_3.type} title={note_3.title} content={note_3.content} color={note_3.color} initialX={0} initialY={0} rotation={note_3.rotation} badge={note_3.badge} stamp={note_3.stamp} /></div>;
}
function _temp5(note_2) {
  return <div key={note_2.id} className="pm-sticky-note-wrapper-in-column"><StickyNote id={note_2.id} type={note_2.type} title={note_2.title} content={note_2.content} color={note_2.color} initialX={0} initialY={0} rotation={note_2.rotation} badge={note_2.badge} stamp={note_2.stamp} /></div>;
}
function _temp4(prev) {
  return !prev;
}
function _temp3(note_1) {
  return note_1.type === "DONE";
}
function _temp2(note_0) {
  return note_0.type === "IN_PROGRESS";
}
function _temp(note) {
  return note.type === "BACKLOG";
}